import { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import Select from "react-select";
import PageSizeDropDownSelection from "../../components/CustomComponents/DropdownSelections/DropDownSelections.jsx";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import "../compnay/ManageCompany.css";
import "./manageRestaurantSettlements.css";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Paid", label: "Paid" },
  { value: "Cancelled", label: "Cancelled" },
];

const HOLD_DAY_OPTIONS = [
  { value: 0, label: "No hold (T+0)" },
  { value: 1, label: "T+1 hold" },
  { value: 2, label: "T+2 hold" },
  { value: 3, label: "T+3 hold" },
];

const toLocalYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const yesterdayYmd = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toLocalYmd(d);
};

const defaultGenerateHold = () => HOLD_DAY_OPTIONS.find((o) => o.value === 0) || HOLD_DAY_OPTIONS[0];

const formatMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return String(d);
  }
};

const statusBadgeClass = (status) => {
  const map = {
    Pending: "bg-warning text-dark",
    Approved: "bg-info",
    Paid: "bg-success",
    Cancelled: "bg-secondary",
    Skipped: "bg-secondary",
  };
  return map[status] || "bg-secondary";
};

const hasBankProfile = (row) => {
  const name = (row?.accountName || "").trim();
  const account = (row?.accountNumber || "").trim();
  const iban = (row?.iban || row?.IBAN || "").trim();
  return Boolean(name && (account || iban));
};

const lineAmount = (line) => Number(line?.amount ?? line?.Amount ?? 0);
const lineType = (line) => (line?.entryType || line?.EntryType || "").toString();

/** Clear payout breakdown from ledger lines (includes Penalty). */
const getSettlementBreakdown = (detail) => {
  const lines = detail?.lines || [];
  const sumTypes = (...types) =>
    lines
      .filter((l) => types.includes(lineType(l)))
      .reduce((sum, l) => sum + lineAmount(l), 0);

  const gross = sumTypes("SalePayable");
  const commission = sumTypes("Commission");
  const promo = sumTypes("PlatformPromo");
  const wastage = sumTypes("Wastage", "WastageRefund");
  const penalty = sumTypes("Penalty");
  const adjustments = sumTypes("Adjustment");
  const costReimbursement = sumTypes("CostReimbursement");
  const net =
    lines.length > 0
      ? lines.reduce((sum, l) => sum + lineAmount(l), 0)
      : Number(detail?.netPayable ?? detail?.NetPayable ?? 0);

  return {
    gross,
    commission,
    promo,
    wastage,
    penalty,
    adjustments,
    costReimbursement,
    net,
  };
};

const formatExportDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toISOString().slice(0, 10);
  } catch {
    return String(d);
  }
};

const getOrderPenalty = (order, lines = []) => {
  const fromOrder = Number(order?.penalty ?? order?.Penalty ?? 0);
  if (fromOrder) return fromOrder;
  const invoice = order?.recvorder_InvoiceNo || order?.Recvorder_InvoiceNo || "";
  if (!invoice) return 0;
  return lines
    .filter(
      (l) =>
        (l.recvorder_InvoiceNo || l.Recvorder_InvoiceNo) === invoice &&
        lineType(l) === "Penalty"
    )
    .reduce((sum, l) => sum + lineAmount(l), 0);
};

/** Build one consolidated order object per invoice (from orders, or from ledger lines). */
const getConsolidatedOrders = (detail) => {
  const lines = detail?.lines || [];
  const orders = detail?.orders || [];
  if (orders.length > 0) return orders;

  const byInvoice = new Map();
  lines.forEach((l) => {
    const invoice = l.recvorder_InvoiceNo || l.Recvorder_InvoiceNo || "(no invoice)";
    if (!byInvoice.has(invoice)) {
      byInvoice.set(invoice, {
        recvorder_InvoiceNo: invoice === "(no invoice)" ? "" : invoice,
        salePayable: 0,
        commission: 0,
        platformPromo: 0,
        wastage: 0,
        wastageRefund: 0,
        penalty: 0,
        adjustment: 0,
        costReimbursement: 0,
        net: 0,
        isCatchUp: false,
      });
    }
    const row = byInvoice.get(invoice);
    const amount = lineAmount(l);
    const type = lineType(l);
    if (type === "SalePayable") row.salePayable += amount;
    else if (type === "Commission") row.commission += amount;
    else if (type === "PlatformPromo") row.platformPromo += amount;
    else if (type === "Wastage") row.wastage += amount;
    else if (type === "WastageRefund") row.wastageRefund += amount;
    else if (type === "Penalty") row.penalty += amount;
    else if (type === "Adjustment") row.adjustment += amount;
    else if (type === "CostReimbursement") row.costReimbursement += amount;
    row.net += amount;
    if (l.isCatchUp || l.IsCatchUp) row.isCatchUp = true;
  });
  return Array.from(byInvoice.values());
};

/** One consolidated row per order. */
const buildOrderExportRows = (detail) => {
  const lines = detail?.lines || [];
  return getConsolidatedOrders(detail).map((o) => [
    o.recvorder_InvoiceNo || "",
    Number(o.salePayable || 0),
    Number(o.commission || 0),
    Number(o.platformPromo || 0),
    Number(o.wastage || 0) + Number(o.wastageRefund || 0),
    getOrderPenalty(o, lines),
    Number(o.adjustment || 0),
    Number(o.costReimbursement || 0),
    Number(o.net || 0),
    o.isCatchUp ? "Yes" : "No",
  ]);
};

/**
 * XLSX: Summary sheet + Orders sheet.
 * Orders sheet = exactly one row per invoice (not separate Penalty/Commission/SalePayable lines).
 */
const downloadSettlementDetailXlsx = (detail) => {
  const breakdown = getSettlementBreakdown(detail);
  const settlementId = detail?.settlement_ID ?? detail?.Settlement_ID ?? "";
  const orderRows = buildOrderExportRows(detail);

  const summarySheet = [
    ["Settlement Summary"],
    [],
    ["Settlement ID", settlementId],
    ["Restaurant", detail?.compName || ""],
    ["CompID", detail?.compID ?? ""],
    ["Status", detail?.status || ""],
    ["Period Start", formatExportDate(detail?.periodStart)],
    ["Period End", formatExportDate(detail?.periodEnd)],
    ["Payment Reference", detail?.paymentReference || ""],
    ["Paid At", formatExportDate(detail?.paidAt)],
    ["Order Count", orderRows.length],
    [],
    ["Payout Breakdown"],
    ["Gross (food)", Number(breakdown.gross || 0)],
    ["Commission", Number(breakdown.commission || 0)],
    ["Promo", Number(breakdown.promo || 0)],
    ["Wastage", Number(breakdown.wastage || 0)],
    ["Penalty", Number(breakdown.penalty || 0)],
    ["Other Adjustments", Number(breakdown.adjustments || 0)],
    ["Cost Reimbursement", Number(breakdown.costReimbursement || 0)],
    ["Net Payable", Number(breakdown.net || 0)],
  ];

  const ordersSheet = [
    [
      "Invoice",
      "Food",
      "Commission",
      "Promo",
      "Wastage",
      "Penalty",
      "Adjustments",
      "CostReimbursement",
      "Net",
      "CatchUp",
    ],
    ...orderRows,
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summarySheet), "Summary");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(ordersSheet), "Orders");
  XLSX.writeFile(wb, `settlement-${settlementId}-orders.xlsx`);
};

const ManageRestaurantSettlements = () => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [loading, setLoading] = useState(false);
  const [settlements, setSettlements] = useState([]);
  const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS[0]);
  const [companyFilter, setCompanyFilter] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [markPaidSettlement, setMarkPaidSettlement] = useState(null);
  const [markPaidRef, setMarkPaidRef] = useState("");
  const [markPaidMethod, setMarkPaidMethod] = useState("BankTransfer");

  const [profileOpen, setProfileOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({
    compID: "",
    accountName: "",
    bankName: "",
    accountNumber: "",
    iban: "",
    sortCode: "",
  });

  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    company: null,
    periodStart: "",
    periodEnd: yesterdayYmd(),
    holdDays: defaultGenerateHold(),
  });
  const [preview, setPreview] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState({
    compID: "",
    amount: "",
    notes: "",
    recvorder_InvoiceNo: "",
  });

  const companyOptions = useMemo(
    () =>
      companies
        .map((c) => {
          const id = c.compID ?? c.CompID;
          if (id == null) return null;
          const name = c.compName || c.CompName || c.name || c.Name || "Company";
          return { value: id, label: `${name} (#${id})` };
        })
        .filter(Boolean),
    [companies]
  );

  const fieldStyle = useMemo(
    () => ({
      borderRadius: "10px",
      border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.14)" : "1px solid #D9D9D9",
      background: isDarkMode ? "#2d3436" : "#F9F9F9",
      color: isDarkMode ? "#ffffff" : "#495057",
    }),
    [isDarkMode]
  );

  const modalDropdownStyles = useMemo(
    () => ({
      ...dropdownStyles,
      menuPortal: (provided) => ({
        ...provided,
        zIndex: 10060,
      }),
      menu: (provided) => ({
        ...(typeof dropdownStyles.menu === "function" ? dropdownStyles.menu(provided) : provided),
        zIndex: 10060,
      }),
    }),
    [dropdownStyles]
  );

  const generateBody = useCallback(() => {
    const body = { holdDays: generateForm.holdDays?.value ?? 0 };
    if (generateForm.company?.value) body.compID = generateForm.company.value;
    if (generateForm.periodStart) body.periodStart = generateForm.periodStart;
    if (generateForm.periodEnd) body.periodEnd = generateForm.periodEnd;
    return body;
  }, [generateForm]);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_COMPANIES, {
        params: { PageSize: 500, PageNumber: 1, SortBy: "CompID", SortOrder: "DESC" },
      });
      if (!response.data?.status) {
        setCompanies([]);
        return;
      }
      const payload = response.data.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.companies)
          ? payload.companies
          : Array.isArray(payload?.Companies)
            ? payload.Companies
            : [];
      setCompanies(list);
    } catch {
      setCompanies([]);
    }
  }, []);

  const fetchSettlements = useCallback(async () => {
    try {
      setLoading(true);
      const params = { PageNumber: pageNumber, PageSize: pageSize.value };
      if (statusFilter?.value) params.Status = statusFilter.value;
      if (companyFilter?.value) params.CompID = companyFilter.value;
      const response = await axiosInstance.get(APIs.GET_RESTAURANT_SETTLEMENTS, { params });
      if (response.data.status) {
        setSettlements(Array.isArray(response.data.data) ? response.data.data : []);
        setTotalRecords(response.data.totalRecords || 0);
        setTotalPages(response.data.totalPages || 1);
      } else {
        showErrorToast(response.data.message || "Failed to load settlements");
      }
    } catch {
      showErrorToast("Failed to load settlements");
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize.value, statusFilter?.value, companyFilter?.value]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    if (generateOpen || adjustOpen) fetchCompanies();
  }, [generateOpen, adjustOpen, fetchCompanies]);

  useEffect(() => {
    const handler = setTimeout(() => fetchSettlements(), 300);
    return () => clearTimeout(handler);
  }, [fetchSettlements]);

  useEffect(() => {
    setPageNumber(1);
  }, [statusFilter, companyFilter, pageSize.value]);

  const validateGenerateRange = () => {
    if (generateForm.periodStart && generateForm.periodEnd && generateForm.periodStart > generateForm.periodEnd) {
      showErrorToast("From date must be on or before to date");
      return false;
    }
    return true;
  };

  const openGenerateModal = () => {
    setGenerateForm({
      company: companyFilter,
      periodStart: "",
      periodEnd: yesterdayYmd(),
      holdDays: defaultGenerateHold(),
    });
    setPreview(null);
    setGenerateOpen(true);
  };

  const updateGenerateForm = (patch) => {
    setGenerateForm((prev) => ({ ...prev, ...patch }));
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!validateGenerateRange()) return;
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_PREVIEW_RESTAURANT_SETTLEMENTS, generateBody());
      if (response.data.status) {
        setPreview(response.data.data);
      } else {
        showErrorToast(response.data.message || "Preview failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!validateGenerateRange()) return;
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_GENERATE_RESTAURANT_SETTLEMENTS, generateBody());
      if (response.data.status) {
        showSuccessToast(response.data.message || "Settlements generated");
        setGenerateOpen(false);
        setPreview(null);
        fetchSettlements();
      } else {
        showErrorToast(response.data.message || "Generate failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Generate failed");
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (row) => {
    const id = row.settlement_ID || row.Settlement_ID;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_RESTAURANT_SETTLEMENT_BY_ID, {
        params: { Settlement_ID: id },
      });
      if (response.data.status) {
        setDetail(response.data.data);
        setDetailOpen(true);
      } else {
        showErrorToast(response.data.message || "Failed to load detail");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load detail");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (row) => {
    const id = row.settlement_ID || row.Settlement_ID;
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_APPROVE_RESTAURANT_SETTLEMENT, {
        settlement_ID: id,
      });
      if (response.data.status) {
        showSuccessToast("Settlement approved");
        fetchSettlements();
        if (detailOpen) openDetail(row);
      } else {
        showErrorToast(response.data.message || "Approve failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Approve failed");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!markPaidSettlement) return;
    if (!markPaidMethod.trim() || !markPaidRef.trim()) {
      showErrorToast("Payment method and reference are required");
      return;
    }
    if (!hasBankProfile(markPaidSettlement)) {
      showErrorToast("Save bank payout details before marking paid");
      return;
    }
    const id = markPaidSettlement.settlement_ID || markPaidSettlement.Settlement_ID;
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_MARK_RESTAURANT_SETTLEMENT_PAID, {
        settlement_ID: id,
        paymentMethod: markPaidMethod.trim(),
        paymentReference: markPaidRef.trim(),
      });
      if (response.data.status) {
        showSuccessToast(response.data.message || "Settlement updated");
        setMarkPaidOpen(false);
        setMarkPaidSettlement(null);
        setMarkPaidRef("");
        fetchSettlements();
        setDetailOpen(false);
      } else {
        showErrorToast(response.data.message || "Mark paid failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Mark paid failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (row) => {
    const id = row.settlement_ID || row.Settlement_ID;
    if (!window.confirm("Cancel this pending settlement and release ledger lines?")) return;
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_CANCEL_RESTAURANT_SETTLEMENT, {
        settlement_ID: id,
      });
      if (response.data.status) {
        showSuccessToast("Settlement cancelled");
        setDetailOpen(false);
        fetchSettlements();
      } else {
        showErrorToast(response.data.message || "Cancel failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Cancel failed");
    } finally {
      setLoading(false);
    }
  };

  const openProfileEditor = (row) => {
    setProfileForm({
      compID: String(row.compID || row.CompID || ""),
      accountName: row.accountName || "",
      bankName: row.bankName || "",
      accountNumber: row.accountNumber || "",
      iban: row.iban || row.IBAN || "",
      sortCode: row.sortCode || "",
    });
    setProfileOpen(true);
  };

  const handleSaveProfile = async () => {
    const compID = parseInt(profileForm.compID, 10);
    if (!compID) {
      showErrorToast("CompID is required");
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_UPSERT_RESTAURANT_PAYOUT_PROFILE, {
        compID,
        accountName: profileForm.accountName || null,
        bankName: profileForm.bankName || null,
        accountNumber: profileForm.accountNumber || null,
        iban: profileForm.iban || null,
        sortCode: profileForm.sortCode || null,
      });
      if (response.data.status) {
        showSuccessToast("Bank profile saved");
        setProfileOpen(false);
        fetchSettlements();
        if (markPaidSettlement && (markPaidSettlement.compID === compID || markPaidSettlement.CompID === compID)) {
          setMarkPaidSettlement({ ...markPaidSettlement, ...response.data.data });
        }
      } else {
        showErrorToast(response.data.message || "Save failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAdjustment = async () => {
    const compID = parseInt(adjustForm.compID, 10);
    const amount = Number(adjustForm.amount);
    if (!compID) {
      showErrorToast("Select a restaurant");
      return;
    }
    if (!amount) {
      showErrorToast("Amount must be non-zero");
      return;
    }
    if (!adjustForm.notes.trim()) {
      showErrorToast("Notes are required");
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_RESTAURANT_SETTLEMENT_ADJUSTMENT, {
        compID,
        amount,
        notes: adjustForm.notes.trim(),
        recvorder_InvoiceNo: adjustForm.recvorder_InvoiceNo.trim() || null,
      });
      if (response.data.status) {
        showSuccessToast(response.data.message || "Adjustment posted");
        setAdjustOpen(false);
        setAdjustForm({ compID: "", amount: "", notes: "", recvorder_InvoiceNo: "" });
      } else {
        showErrorToast(response.data.message || "Adjustment failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Adjustment failed");
    } finally {
      setLoading(false);
    }
  };

  const exportDetailXlsx = (row) => {
    if (!row) {
      showErrorToast("No settlement detail to export");
      return;
    }
    const hasOrders = (row.orders || []).length > 0;
    const hasLines = (row.lines || []).length > 0;
    if (!hasOrders && !hasLines) {
      showErrorToast("No order or ledger rows to export");
      return;
    }
    downloadSettlementDetailXlsx(row);
  };

  const detailBreakdown = useMemo(
    () => (detail ? getSettlementBreakdown(detail) : null),
    [detail]
  );

  const headerButtonBase = useMemo(
    () => ({
      height: "40px",
      padding: "8px 16px",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "600",
      lineHeight: "1.25",
      whiteSpace: "nowrap",
    }),
    []
  );

  const outlineButtonStyle = useMemo(
    () => ({
      ...headerButtonBase,
      backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "#ffffff",
      border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.28)" : `1px solid ${themeColor}`,
      color: isDarkMode ? "#ffffff" : themeColor,
    }),
    [headerButtonBase, isDarkMode, themeColor]
  );

  const secondaryButtonStyle = useMemo(
    () => ({
      ...headerButtonBase,
      backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.14)" : "#eef2f7",
      border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.22)" : "1px solid #cbd5e1",
      color: isDarkMode ? "#ffffff" : "#1f2937",
    }),
    [headerButtonBase, isDarkMode]
  );

  const primaryButtonStyle = useMemo(
    () => ({
      ...headerButtonBase,
      backgroundColor: themeColor,
      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
      border: "none",
      color: "#ffffff",
      boxShadow: `0 2px 8px ${themeColor}40`,
    }),
    [headerButtonBase, themeColor]
  );

  return (
    <div>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Restaurant Settlements</li>
          </ol>
          <h1 className="page-header mb-0">Restaurant Settlements</h1>
          <p className="text-muted mb-0 small">
            Browse existing batches below. Use Generate to create a settlement from unsettled payable orders.
            Approve first, then mark paid.
          </p>
        </div>
        <div className="ms-auto d-flex gap-2 flex-wrap">
          <button type="button" className="btn sr-header-btn sr-header-btn-outline" onClick={() => setAdjustOpen(true)} style={outlineButtonStyle}>
            <i className="fa fa-sliders me-2" aria-hidden="true" />
            Add adjustment
          </button>
          <button type="button" className="btn px-4 sr-header-btn sr-header-btn-primary" onClick={openGenerateModal} style={primaryButtonStyle}>
            <i className="fa fa-calendar-plus fa-lg me-2 ms-n2" aria-hidden="true" />
            Generate
          </button>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active">
            <div className="mb-4">
              <div
                className="card filter-card"
                style={{
                  position: "relative",
                  zIndex: 1,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: `0 4px 20px ${themeColor}40`,
                }}
              >
                <div className="card-body p-3">
                  <div className="row g-3 align-items-center">
                    <div className="col-xl-4 col-lg-4 col-md-6">
                      <Select
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={STATUS_OPTIONS}
                        styles={dropdownStyles}
                        placeholder="All statuses"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </div>
                    <div className="col-xl-5 col-lg-5 col-md-6">
                      <Select
                        value={companyFilter}
                        onChange={setCompanyFilter}
                        options={companyOptions}
                        styles={dropdownStyles}
                        placeholder="Filter by restaurant"
                        isClearable
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </div>
                    <div className="col-xl-3 col-lg-3 col-md-6">
                      <PageSizeDropDownSelection
                        defaultValue={pageSize}
                        onChange={setPageSize}
                        styles={dropdownStyles}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Company</th>
                    <th>Period</th>
                    <th>Orders</th>
                    <th>Gross</th>
                    <th>Commission</th>
                    <th>Net</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-4">
                        No settlements yet. Click Generate to create a batch from unsettled orders.
                      </td>
                    </tr>
                  ) : (
                    settlements.map((row) => {
                      const id = row.settlement_ID || row.Settlement_ID;
                      const status = row.status || row.Status || "";
                      const net = Number(row.netPayable || 0);
                      return (
                        <tr key={id}>
                          <td>{id}</td>
                          <td>
                            {row.compName || "—"}
                            <div className="small text-muted">#{row.compID}</div>
                          </td>
                          <td>
                            {formatDate(row.periodStart)} – {formatDate(row.periodEnd)}
                          </td>
                          <td>{row.orderCount ?? 0}</td>
                          <td>{formatMoney(row.grossSales)}</td>
                          <td>{formatMoney(row.commissionTotal)}</td>
                          <td>
                            <strong>{formatMoney(row.netPayable)}</strong>
                          </td>
                          <td>
                            <span className={`badge ${statusBadgeClass(status)}`}>{status}</span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => openDetail(row)}
                            >
                              View
                            </button>
                            {status === "Pending" && (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success me-1"
                                  onClick={() => handleApprove(row)}
                                >
                                  Approve
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary me-1"
                                  onClick={() => handleCancel(row)}
                                >
                                  Cancel
                                </button>
                              </>
                            )}
                            {status === "Approved" && (
                              <button
                                type="button"
                                className="btn btn-sm btn-success"
                                onClick={() => {
                                  setMarkPaidSettlement(row);
                                  setMarkPaidOpen(true);
                                }}
                              >
                                {net < 0 ? "Mark collected" : "Mark paid"}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <Pagination
                totalRecords={totalRecords}
                pageSize={parseInt(pageSize.value, 10) || 50}
                pageNumber={pageNumber}
                totalPages={totalPages}
                setPageNumber={setPageNumber}
                themeColor={themeColor}
              />
            )}
          </div>
        </div>
      </div>

      <CustomModal
        title={detail ? `Settlement #${detail.settlement_ID}` : "Settlement"}
        subtitle={
          detail
            ? `${detail.compName || "Restaurant"} (#${detail.compID}) · ${formatDate(detail.periodStart)} – ${formatDate(detail.periodEnd)}`
            : ""
        }
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        isCentered={true}
        modalSize="modal-lg"
        maxHeight="90vh"
      >
        {detail && (
          <div>
            <div className="sr-detail-kpis">
              <div className="sr-detail-kpi">
                <div className="sr-detail-kpi-label">Gross (food)</div>
                <div className="sr-detail-kpi-value">{formatMoney(detailBreakdown?.gross)}</div>
              </div>
              <div className="sr-detail-kpi">
                <div className="sr-detail-kpi-label">Commission</div>
                <div className="sr-detail-kpi-value">{formatMoney(detailBreakdown?.commission)}</div>
              </div>
              <div className="sr-detail-kpi">
                <div className="sr-detail-kpi-label">Promo</div>
                <div className="sr-detail-kpi-value">{formatMoney(detailBreakdown?.promo)}</div>
              </div>
              <div className="sr-detail-kpi">
                <div className="sr-detail-kpi-label">Wastage</div>
                <div className="sr-detail-kpi-value">{formatMoney(detailBreakdown?.wastage)}</div>
              </div>
              <div className="sr-detail-kpi">
                <div className="sr-detail-kpi-label">Penalty</div>
                <div
                  className={`sr-detail-kpi-value ${
                    Number(detailBreakdown?.penalty || 0) < 0 ? "text-danger" : ""
                  }`}
                >
                  {formatMoney(detailBreakdown?.penalty)}
                </div>
              </div>
              <div className="sr-detail-kpi">
                <div className="sr-detail-kpi-label">Other adj.</div>
                <div className="sr-detail-kpi-value">
                  {formatMoney(
                    (detailBreakdown?.adjustments || 0) +
                      (detailBreakdown?.costReimbursement || 0)
                  )}
                </div>
              </div>
              <div className="sr-detail-kpi is-accent" style={{ backgroundColor: themeColor }}>
                <div className="sr-detail-kpi-label">Net payable</div>
                <div className="sr-detail-kpi-value">{formatMoney(detailBreakdown?.net)}</div>
              </div>
            </div>

            <div className="sr-detail-grid">
              <div className="sr-detail-card">
                <div className="sr-detail-card-head">
                  <h6>Settlement</h6>
                  <span className={`badge ${statusBadgeClass(detail.status)}`}>{detail.status}</span>
                </div>
                <div className="sr-detail-kv">
                  <span>Restaurant</span>
                  <span>
                    {detail.compName || "—"} (#{detail.compID})
                  </span>
                </div>
                <div className="sr-detail-kv">
                  <span>Period</span>
                  <span>
                    {formatDate(detail.periodStart)} – {formatDate(detail.periodEnd)}
                  </span>
                </div>
                {detail.paymentReference ? (
                  <div className="sr-detail-kv">
                    <span>Payment ref</span>
                    <span>{detail.paymentReference}</span>
                  </div>
                ) : null}
                {detail.paidAt ? (
                  <div className="sr-detail-kv">
                    <span>Paid</span>
                    <span>{formatDate(detail.paidAt)}</span>
                  </div>
                ) : null}
                {detail.paymentMethod ? (
                  <div className="sr-detail-kv">
                    <span>Method</span>
                    <span>{detail.paymentMethod}</span>
                  </div>
                ) : null}
              </div>

              <div className="sr-detail-card">
                <div className="sr-detail-card-head">
                  <h6>Payout bank details</h6>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => openProfileEditor(detail)}
                  >
                    Edit
                  </button>
                </div>
                {hasBankProfile(detail) ? (
                  <>
                    <div className="sr-detail-kv">
                      <span>Account name</span>
                      <span>{detail.accountName || "—"}</span>
                    </div>
                    <div className="sr-detail-kv">
                      <span>Bank</span>
                      <span>{detail.bankName || "—"}</span>
                    </div>
                    <div className="sr-detail-kv">
                      <span>Account number</span>
                      <span>{detail.accountNumber || "—"}</span>
                    </div>
                    <div className="sr-detail-kv">
                      <span>IBAN</span>
                      <span>{detail.iban || "—"}</span>
                    </div>
                    <div className="sr-detail-kv">
                      <span>Sort code</span>
                      <span>{detail.sortCode || "—"}</span>
                    </div>
                  </>
                ) : (
                  <div className="text-muted small">No bank profile saved yet.</div>
                )}
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <div>
                <h6 className="mb-0">Orders</h6>
                <div className="text-muted small">
                  {(detail.orders || []).length}{" "}
                  {(detail.orders || []).length === 1 ? "order" : "orders"}
                  {" · one row per invoice"}
                </div>
              </div>
              <button
                type="button"
                className="sr-export-btn"
                style={{ backgroundColor: themeColor }}
                onClick={() => exportDetailXlsx(detail)}
              >
                Export XLSX
              </button>
            </div>

            <div className="sr-detail-table-wrap">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th className="sr-num">Food</th>
                    <th className="sr-num">Commission</th>
                    <th className="sr-num">Promo</th>
                    <th className="sr-num">Wastage</th>
                    <th className="sr-num">Penalty</th>
                    <th className="sr-num">Adj.</th>
                    <th className="sr-num">Cost reimb.</th>
                    <th className="sr-num">Net</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.orders || []).length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-muted text-center py-4">
                        No orders
                      </td>
                    </tr>
                  ) : (
                    detail.orders.map((order) => {
                      const penalty = getOrderPenalty(order, detail.lines || []);
                      return (
                        <tr key={order.recvorder_InvoiceNo || "none"}>
                          <td>
                            <span className="sr-invoice" title={order.recvorder_InvoiceNo || ""}>
                              {order.recvorder_InvoiceNo || "—"}
                            </span>
                          </td>
                          <td className="sr-num">{formatMoney(order.salePayable)}</td>
                          <td className="sr-num">{formatMoney(order.commission)}</td>
                          <td className="sr-num">{formatMoney(order.platformPromo)}</td>
                          <td className="sr-num">
                            {formatMoney((order.wastage || 0) + (order.wastageRefund || 0))}
                          </td>
                          <td className="sr-num">
                            <span className={penalty < 0 ? "text-danger" : ""}>
                              {formatMoney(penalty)}
                            </span>
                          </td>
                          <td className="sr-num">{formatMoney(order.adjustment)}</td>
                          <td className="sr-num">{formatMoney(order.costReimbursement)}</td>
                          <td className="sr-num">
                            <strong>{formatMoney(order.net)}</strong>
                          </td>
                          <td>
                            {order.isCatchUp ? (
                              <span className="badge bg-warning text-dark">Prior period</span>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CustomModal>

      <CustomModal
        title="Generate restaurant settlement"
        subtitle="Choose restaurant, order dates, and hold. Preview first, then generate."
        isOpen={generateOpen}
        onClose={() => {
          setGenerateOpen(false);
          setPreview(null);
        }}
        onSubmit={handleGenerate}
        showButton={true}
        submitButtonText="Generate"
        submitButtonDisabled={loading}
        isCentered={true}
        modalSize="modal-lg"
        maxHeight="90vh"
        zIndex={10050}
      >
        <div className={`sr-preview-modal ${isDarkMode ? "is-dark" : "is-light"}`}>
          <div className="row g-3 mb-3">
            <div className="col-12">
              <label className="sr-generate-label">Restaurant</label>
              <Select
                value={generateForm.company}
                onChange={(opt) => updateGenerateForm({ company: opt })}
                options={companyOptions}
                styles={modalDropdownStyles}
                placeholder="All restaurants"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <div className="sr-generate-hint">Leave empty to generate for every restaurant with unsettled orders.</div>
            </div>
            <div className="col-md-4">
              <label className="sr-generate-label">From date</label>
              <input
                type="date"
                className="form-control"
                value={generateForm.periodStart}
                onChange={(e) => updateGenerateForm({ periodStart: e.target.value })}
                style={fieldStyle}
              />
              <div className="sr-generate-hint">Optional. Empty includes older unsettled lines.</div>
            </div>
            <div className="col-md-4">
              <label className="sr-generate-label">To date</label>
              <input
                type="date"
                className="form-control"
                value={generateForm.periodEnd}
                onChange={(e) => updateGenerateForm({ periodEnd: e.target.value })}
                style={fieldStyle}
              />
              <div className="sr-generate-hint">Defaults to yesterday.</div>
            </div>
            <div className="col-md-4">
              <label className="sr-generate-label">Hold days</label>
              <Select
                value={generateForm.holdDays}
                onChange={(opt) => updateGenerateForm({ holdDays: opt || defaultGenerateHold() })}
                options={HOLD_DAY_OPTIONS}
                styles={modalDropdownStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <div className="sr-generate-hint">Use No hold (T+0) to include yesterday’s orders today.</div>
            </div>
          </div>

          <div className="d-flex justify-content-end mb-3">
            <button
              type="button"
              className="btn sr-header-btn sr-header-btn-secondary"
              onClick={handlePreview}
              disabled={loading}
              style={secondaryButtonStyle}
            >
              <i className="fa fa-eye me-2" aria-hidden="true" />
              Preview
            </button>
          </div>

          {preview && (
            <>
              <div className="sr-preview-callout">
                <i className="fa fa-calendar-check-o me-2" aria-hidden="true" />
                <span>
                  Period <strong>{formatDate(preview.periodStart)}</strong> – <strong>{formatDate(preview.periodEnd)}</strong>
                  {" · "}
                  Hold cutoff <strong>{formatDate(preview.holdCutoffDate)}</strong>
                </span>
              </div>

              <div className="sr-preview-kpis">
                <div className="sr-preview-kpi">
                  <div className="sr-preview-kpi-label">New settlements</div>
                  <div className="sr-preview-kpi-value">{preview.settlementsCreated ?? 0}</div>
                </div>
                <div className="sr-preview-kpi">
                  <div className="sr-preview-kpi-label">Pending updates</div>
                  <div className="sr-preview-kpi-value">{preview.settlementsUpdated ?? 0}</div>
                </div>
                <div className="sr-preview-kpi">
                  <div className="sr-preview-kpi-label">In-range lines</div>
                  <div className="sr-preview-kpi-value">{preview.inWeekLinesAdded ?? 0}</div>
                </div>
                <div className="sr-preview-kpi">
                  <div className="sr-preview-kpi-label">Catch-up lines</div>
                  <div className="sr-preview-kpi-value">{preview.orphanLinesAdded ?? 0}</div>
                </div>
                <div className="sr-preview-kpi">
                  <div className="sr-preview-kpi-label">Skipped</div>
                  <div className="sr-preview-kpi-value">{preview.skippedApprovedPaid ?? 0}</div>
                </div>
              </div>

              <div className="sr-preview-table-head">
                <h6 className="mb-0">Restaurants in this batch</h6>
                <span className="sr-preview-table-count">{(preview.issued || []).length} row(s)</span>
              </div>

              <div className="sr-preview-table-wrap">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th className="sr-num">Net</th>
                      <th className="text-center">In range</th>
                      <th className="text-center">Catch-up</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.issued || []).length === 0 ? (
                      <tr>
                        <td colSpan={5} className="sr-preview-empty text-center py-4">
                          No settlements would be created for this period.
                        </td>
                      </tr>
                    ) : (
                      (preview.issued || []).map((row) => (
                        <tr key={`${row.compID}-${row.settlement_ID}`}>
                          <td>
                            <div className="fw-semibold">{row.compName || "—"}</div>
                            <div className="sr-preview-muted">#{row.compID}</div>
                          </td>
                          <td className="sr-num fw-semibold">{formatMoney(row.netPayable)}</td>
                          <td className="text-center">{row.inWeekLines ?? 0}</td>
                          <td className="text-center">{row.orphanLines ?? 0}</td>
                          <td>
                            <span className={`badge ${statusBadgeClass(row.status)}`}>{row.status || "Pending"}</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </CustomModal>

      <CustomModal
        title={Number(markPaidSettlement?.netPayable || 0) < 0 ? "Mark settlement collected" : "Mark settlement paid"}
        isOpen={markPaidOpen}
        onClose={() => setMarkPaidOpen(false)}
        isCentered={true}
      >
        {markPaidSettlement && (
          <>
            <p>
              Confirm {Number(markPaidSettlement.netPayable) < 0 ? "collection" : "payout"} of{" "}
              <strong>{formatMoney(markPaidSettlement.netPayable)}</strong> for {markPaidSettlement.compName} (#{markPaidSettlement.compID}).
            </p>
            {!hasBankProfile(markPaidSettlement) && (
              <div className="alert alert-warning py-2">
                Bank details are required.{" "}
                <button type="button" className="btn btn-link p-0" onClick={() => openProfileEditor(markPaidSettlement)}>
                  Edit payout profile
                </button>
              </div>
            )}
            <div className="small mb-3">
              <div>{markPaidSettlement.accountName || "No account name"}</div>
              <div>{markPaidSettlement.accountNumber || markPaidSettlement.iban || "No account / IBAN"}</div>
            </div>
            <div className="mb-3">
              <label className="form-label">Payment method</label>
              <input
                className="form-control"
                value={markPaidMethod}
                onChange={(e) => setMarkPaidMethod(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Payment reference</label>
              <input
                className="form-control"
                value={markPaidRef}
                onChange={(e) => setMarkPaidRef(e.target.value)}
                placeholder="Bank ref / transfer ID"
              />
            </div>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleMarkPaid}
              disabled={!markPaidMethod.trim() || !markPaidRef.trim() || !hasBankProfile(markPaidSettlement)}
            >
              Confirm
            </button>
          </>
        )}
      </CustomModal>

      <CustomModal
        title="Restaurant payout bank profile"
        isOpen={profileOpen}
        onClose={() => setProfileOpen(false)}
        isCentered={true}
      >
        <div className="mb-2">
          <label className="form-label">CompID</label>
          <input className="form-control" value={profileForm.compID} disabled />
        </div>
        <div className="mb-2">
          <label className="form-label">Account name</label>
          <input
            className="form-control"
            value={profileForm.accountName}
            onChange={(e) => setProfileForm({ ...profileForm, accountName: e.target.value })}
          />
        </div>
        <div className="mb-2">
          <label className="form-label">Bank name</label>
          <input
            className="form-control"
            value={profileForm.bankName}
            onChange={(e) => setProfileForm({ ...profileForm, bankName: e.target.value })}
          />
        </div>
        <div className="mb-2">
          <label className="form-label">Account number</label>
          <input
            className="form-control"
            value={profileForm.accountNumber}
            onChange={(e) => setProfileForm({ ...profileForm, accountNumber: e.target.value })}
          />
        </div>
        <div className="mb-2">
          <label className="form-label">IBAN</label>
          <input
            className="form-control"
            value={profileForm.iban}
            onChange={(e) => setProfileForm({ ...profileForm, iban: e.target.value })}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Sort code</label>
          <input
            className="form-control"
            value={profileForm.sortCode}
            onChange={(e) => setProfileForm({ ...profileForm, sortCode: e.target.value })}
          />
        </div>
        <button type="button" className="btn btn-primary" onClick={handleSaveProfile}>
          Save profile
        </button>
      </CustomModal>

      <CustomModal
        title="Post restaurant adjustment"
        subtitle="Manual credit or debit on the restaurant payable ledger"
        isOpen={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        onSubmit={handleSaveAdjustment}
        showButton={true}
        submitButtonText="Post adjustment"
        isCentered={true}
        modalSize="modal-lg"
        zIndex={10050}
      >
        <div className={`sr-adjust-modal ${isDarkMode ? "is-dark" : "is-light"}`}>
          <div className="sr-adjust-callout">
            <i className="fa fa-info-circle me-2" aria-hidden="true" />
            Positive amount increases what the platform owes the restaurant. Negative decreases it.
            Applies on the next generate. Adjustments skip sale hold.
          </div>

          <div className="mb-3">
            <label className="sr-adjust-label">Restaurant</label>
            <Select
              value={companyOptions.find((o) => String(o.value) === String(adjustForm.compID)) || null}
              onChange={(opt) => setAdjustForm({ ...adjustForm, compID: opt ? String(opt.value) : "" })}
              options={companyOptions}
              styles={modalDropdownStyles}
              placeholder={companyOptions.length ? "Select restaurant" : "No restaurants loaded"}
              isDisabled={!companyOptions.length}
              noOptionsMessage={() => (companies.length ? "No matching restaurant" : "No restaurants available")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {!companyOptions.length && (
              <div className="sr-adjust-help mt-2">
                Could not load restaurants. Close and reopen, or refresh the page.
              </div>
            )}
          </div>

          <div className="mb-3">
            <label className="sr-adjust-label">Amount</label>
            <input
              type="number"
              step="0.01"
              className="form-control sr-adjust-input"
              style={fieldStyle}
              placeholder="e.g. 500 or -120.50"
              value={adjustForm.amount}
              onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })}
            />
            <div className="sr-adjust-help">Use negative values to reduce what is owed to the restaurant.</div>
          </div>

          <div className="mb-3">
            <label className="sr-adjust-label">Invoice (optional)</label>
            <input
              className="form-control sr-adjust-input"
              style={fieldStyle}
              placeholder="Link to a specific order invoice"
              value={adjustForm.recvorder_InvoiceNo}
              onChange={(e) => setAdjustForm({ ...adjustForm, recvorder_InvoiceNo: e.target.value })}
            />
          </div>

          <div className="mb-0">
            <label className="sr-adjust-label">Notes</label>
            <textarea
              className="form-control sr-adjust-input"
              style={fieldStyle}
              rows={3}
              placeholder="Reason for this adjustment (required)"
              value={adjustForm.notes}
              onChange={(e) => setAdjustForm({ ...adjustForm, notes: e.target.value })}
            />
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default ManageRestaurantSettlements;
